// frontend/src/components/announcements/AnnouncementModal.jsx
// Modal that displays active announcements when user logs in

import React, { useState, useEffect, useCallback } from "react";
import secureAxios from "../../utils/secureAxios";
import { useAuth } from "../../contexts/AuthContext";
import "./announcements.css";

const AnnouncementModal = () => {
  const { user, isAuthenticated } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch active announcements on login
  const fetchAnnouncements = useCallback(async () => {
    if (!isAuthenticated || !user?.user_id) {
      setLoading(false);
      return;
    }

    try {
      const response = await secureAxios.get("/api/announcements/active");
      const activeAnnouncements = response.data || [];

      setAnnouncements(activeAnnouncements);
      setShow(activeAnnouncements.length > 0);
      setCurrentIndex(0);
    } catch (err) {
      console.error("Error fetching announcements:", err);
      // Don't show error to user - announcements are not critical
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.user_id]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const currentAnnouncement = announcements[currentIndex];

  // Move to next announcement or close modal
  const goToNextOrClose = () => {
    if (currentIndex < announcements.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      setShow(false);
    }
  };

  // Handle "Got it!" action - mark as viewed
  const handleViewed = async () => {
    if (!currentAnnouncement) return;

    setActionLoading(true);
    try {
      await secureAxios.post(
        `/api/announcements/${currentAnnouncement.announcement_id}/viewed`
      );
      goToNextOrClose();
    } catch (err) {
      console.error("Error marking announcement as viewed:", err);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle dismiss action - permanently hide
  const handleDismiss = async () => {
    if (!currentAnnouncement) return;

    setActionLoading(true);
    try {
      await secureAxios.post(
        `/api/announcements/${currentAnnouncement.announcement_id}/dismiss`
      );
      goToNextOrClose();
    } catch (err) {
      console.error("Error dismissing announcement:", err);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle read later action
  const handleReadLater = async (hours = 24) => {
    if (!currentAnnouncement) return;

    setActionLoading(true);
    try {
      await secureAxios.post(
        `/api/announcements/${currentAnnouncement.announcement_id}/read-later`,
        {
          hours,
        }
      );
      goToNextOrClose();
    } catch (err) {
      console.error("Error setting read later:", err);
    } finally {
      setActionLoading(false);
    }
  };

  // Get priority badge styling
  const getPriorityBadge = (priority) => {
    const badges = {
      urgent: {
        class: "bg-danger",
        icon: "bi-exclamation-triangle-fill",
        text: "Urgent",
      },
      high: {
        class: "bg-warning text-dark",
        icon: "bi-exclamation-circle-fill",
        text: "Important",
      },
      normal: {
        class: "bg-info",
        icon: "bi-info-circle-fill",
        text: "Announcement",
      },
      low: { class: "bg-secondary", icon: "bi-bell", text: "Info" },
    };
    return badges[priority] || badges.normal;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading || !show || !currentAnnouncement) {
    return null;
  }

  const priority = getPriorityBadge(currentAnnouncement.priority);
  const hasMultiple = announcements.length > 1;

  return (
    <div
      className="modal show d-block announcement-modal"
      style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
      tabIndex="-1"
      role="dialog"
      aria-labelledby="announcementModalTitle"
      aria-modal="true"
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          {/* Header */}
          <div className={`modal-header ${priority.class} text-white`}>
            <h5 className="modal-title" id="announcementModalTitle">
              <i className={`bi ${priority.icon} me-2`}></i>
              {priority.text}
              {hasMultiple && (
                <span className="badge bg-light text-dark ms-2">
                  {currentIndex + 1} of {announcements.length}
                </span>
              )}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleViewed}
              disabled={actionLoading}
              aria-label="Close"
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body">
            <h4 className="announcement-title mb-3">
              {currentAnnouncement.title}
            </h4>

            <div
              className="announcement-content mb-4"
              dangerouslySetInnerHTML={{
                __html: currentAnnouncement.content.replace(/\n/g, "<br/>"),
              }}
            />

            <div className="announcement-meta text-muted small">
              <span>
                <i className="bi bi-calendar3 me-1"></i>
                Posted: {formatDate(currentAnnouncement.created_at)}
              </span>
              {currentAnnouncement.end_date && (
                <span className="ms-3">
                  <i className="bi bi-clock me-1"></i>
                  Expires: {formatDate(currentAnnouncement.end_date)}
                </span>
              )}
              {currentAnnouncement.created_by_name && (
                <span className="ms-3">
                  <i className="bi bi-person me-1"></i>
                  By: {currentAnnouncement.created_by_name}
                </span>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer justify-content-between">
            {/* Left side - Options dropdown */}
            <div className="d-flex gap-2">
              {/* Remind Me Later dropdown */}
              <div className="dropdown">
                <button
                  className="btn btn-outline-secondary dropdown-toggle"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  disabled={actionLoading}
                >
                  <i className="bi bi-clock-history me-1"></i>
                  Remind Me Later
                </button>
                <ul className="dropdown-menu">
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleReadLater(1)}
                      disabled={actionLoading}
                    >
                      <i className="bi bi-hourglass me-2"></i>
                      In 1 hour
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleReadLater(4)}
                      disabled={actionLoading}
                    >
                      <i className="bi bi-hourglass-split me-2"></i>
                      In 4 hours
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleReadLater(24)}
                      disabled={actionLoading}
                    >
                      <i className="bi bi-calendar-day me-2"></i>
                      Tomorrow
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleReadLater(168)}
                      disabled={actionLoading}
                    >
                      <i className="bi bi-calendar-week me-2"></i>
                      Next week
                    </button>
                  </li>
                </ul>
              </div>

              {/* Don't Show Again button */}
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={handleDismiss}
                disabled={actionLoading}
                title="Don't show this announcement again"
              >
                <i className="bi bi-x-circle me-1"></i>
                Don't Show Again
              </button>
            </div>

            {/* Right side - Got it button (marks as viewed) */}
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleViewed}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Processing...
                </>
              ) : (
                <>
                  <i className="bi bi-check-lg me-1"></i>
                  {hasMultiple && currentIndex < announcements.length - 1
                    ? "Got it, Next"
                    : "Got it!"}
                </>
              )}
            </button>
          </div>

          {/* Progress indicator for multiple announcements */}
          {hasMultiple && (
            <div className="announcement-progress">
              {announcements.map((_, idx) => (
                <span
                  key={idx}
                  className={`progress-dot ${
                    idx === currentIndex ? "active" : ""
                  } ${idx < currentIndex ? "completed" : ""}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnouncementModal;
